import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Autocomplete,
  Chip,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { getBrands } from '../../services/brandService';
import { createPlannedReport } from '../../services/reportService';
import { useAuth } from '../../hooks/useAuth';
import {
  PAGE_TYPES,
  CHANGE_TYPES,
  SUGGESTED_TAGS,
} from '../../utils/reportTaxonomy';
import showNotification from '../common/Notification';
import { BRAND } from '../../theme/brandTheme';

/**
 * Dialog — save pre-test calculator results as a Taslak report
 */
const PlanTestDialog = ({
  open,
  onClose,
  preTestData,
  preTestResults,
  onCreated,
}) => {
  const { user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    reportName: '',
    brand: '',
    hypothesis: '',
    tags: [],
    pageType: '',
    changeType: '',
  });

  // Load brands the user can write to
  useEffect(() => {
    if (!open || !user) return;

    const load = async () => {
      try {
        if (user.role === 'admin' || user.role === 'editor') {
          const list = await getBrands();
          setBrands(list || []);
        } else {
          setBrands(user.brands || []);
        }
      } catch {
        setBrands([]);
      }
    };
    load();
  }, [open, user]);

  const handleSubmit = async () => {
    if (!form.reportName.trim() || !form.brand) {
      showNotification('Rapor adı ve marka zorunludur', 'warning');
      return;
    }

    try {
      setSaving(true);
      const report = await createPlannedReport({
        reportName: form.reportName.trim(),
        brand: form.brand,
        hypothesis: form.hypothesis,
        tags: form.tags,
        pageType: form.pageType || null,
        changeType: form.changeType || null,
        preTestData,
        preTestResults,
      });
      showNotification('Taslak test planı oluşturuldu', 'success');
      onCreated?.(report);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.error || 'Taslak oluşturulamadı';
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: BRAND.purple }}>
        <EventNoteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Test Planla — Taslak Oluştur
      </DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <TextField
          label="Rapor adı"
          value={form.reportName}
          onChange={(e) => setForm((prev) => ({ ...prev, reportName: e.target.value }))}
          placeholder="IP-501 Checkout CTA Testi"
          fullWidth
          required
          size="small"
        />
        <TextField
          select
          label="Marka"
          value={form.brand}
          onChange={(e) => setForm((prev) => ({ ...prev, brand: e.target.value }))}
          fullWidth
          required
          size="small"
        >
          {brands.map((b) => (
            <MenuItem key={b._id} value={b._id}>
              {b.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Hipotez"
          value={form.hypothesis}
          onChange={(e) => setForm((prev) => ({ ...prev, hypothesis: e.target.value }))}
          multiline
          minRows={2}
          fullWidth
          size="small"
        />
        <TextField
          select
          label="Sayfa tipi"
          value={form.pageType}
          onChange={(e) => setForm((prev) => ({ ...prev, pageType: e.target.value }))}
          fullWidth
          size="small"
        >
          <MenuItem value="">Seçilmedi</MenuItem>
          {PAGE_TYPES.map((p) => (
            <MenuItem key={p.value} value={p.value}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Değişiklik tipi"
          value={form.changeType}
          onChange={(e) => setForm((prev) => ({ ...prev, changeType: e.target.value }))}
          fullWidth
          size="small"
        >
          <MenuItem value="">Seçilmedi</MenuItem>
          {CHANGE_TYPES.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>
        <Autocomplete
          multiple
          freeSolo
          options={SUGGESTED_TAGS}
          value={form.tags}
          onChange={(_, value) => setForm((prev) => ({ ...prev, tags: value }))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Etiketler" size="small" placeholder="checkout, cta..." />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>İptal</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          sx={{ bgcolor: BRAND.purple, '&:hover': { bgcolor: '#3d1c6b' } }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Taslak Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanTestDialog;
